import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AssetCollection, MediaAsset } from "../../../shared/types";
import type { CrawlJob } from "../../../shared/crawl";
import { SIGNED_URL_QUERY_OPTIONS, SIGNED_URL_REFETCH_MS, signedUrlRefetchMs } from "../lib/signed-url-refresh";

export interface ShopifyIntegration {
  id: string;
  platform: "shopify";
  platform_username: string | null;
  status: string;
  shop: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShopifyImportEstimateResponse {
  data: {
    estimate: {
      product_count: number;
      est_image_count: number;
      est_caption_cost_cents: number;
    };
    requires_confirm: boolean;
    job_preview_id: string;
  };
}

export function useMediaList(brandId: string | undefined, params: URLSearchParams) {
  const qs = params.toString();
  return useQuery({
    queryKey: ["media", brandId, qs],
    queryFn: () => api.get<{ data: MediaAsset[]; next_cursor?: string }>(`/api/brands/${brandId}/media${qs ? `?${qs}` : ""}`),
    enabled: !!brandId,
    // Rows carry signed thumbnail URLs (~1h); refresh before they expire.
    ...SIGNED_URL_QUERY_OPTIONS,
    refetchInterval: (query) => signedUrlRefetchMs((query.state.data?.data ?? []).map((asset) => asset.url_expires_at)),
  });
}

export function useInfiniteMediaList(brandId: string | undefined, params: URLSearchParams) {
  const qs = params.toString();
  return useInfiniteQuery({
    queryKey: ["media-infinite", brandId, qs],
    queryFn: ({ pageParam }) => {
      const next = new URLSearchParams(qs);
      if (typeof pageParam === "string" && pageParam) {
        next.set("cursor", pageParam);
      }
      return api.get<{ data: MediaAsset[]; next_cursor?: string }>(`/api/brands/${brandId}/media${next.toString() ? `?${next.toString()}` : ""}`);
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!brandId,
    ...SIGNED_URL_QUERY_OPTIONS,
    refetchInterval: (query) => query.state.data?.pages.some((page) =>
      page.data.some((asset) => asset.processing_status === "pending" || asset.processing_status === "processing"),
    ) ? 3000 : signedUrlRefetchMs((query.state.data?.pages ?? []).flatMap((page) => page.data.map((asset) => asset.url_expires_at))),
  });
}

export function useCollections(brandId: string | undefined) {
  return useQuery({
    queryKey: ["media-collections", brandId],
    queryFn: () => api.get<{ data: AssetCollection[] }>(`/api/brands/${brandId}/collections`),
    enabled: !!brandId,
    // Cover images are signed URLs (~1h).
    ...SIGNED_URL_QUERY_OPTIONS,
    refetchInterval: SIGNED_URL_REFETCH_MS,
  });
}

export function useUploadMedia(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ formData, onProgress }: { formData: FormData; onProgress?: (progress: number) => void }) =>
      api.upload<{ data: { created: MediaAsset[]; deduped: MediaAsset[]; restored: MediaAsset[]; collection_id?: string } }>(
        `/api/brands/${brandId}/media/upload`,
        formData,
        onProgress,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media-infinite", brandId] });
      qc.invalidateQueries({ queryKey: ["media", brandId] });
      qc.invalidateQueries({ queryKey: ["media-collections", brandId] });
    },
  });
}

export function useImportMediaUrls(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { urls: string[]; collection_id?: string; asset_type?: string }) =>
      api.post<{ data: { created: MediaAsset[]; deduped: MediaAsset[]; failed: Array<{ url: string; reason: string }> } }>(
        `/api/brands/${brandId}/media/import-url`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media-infinite", brandId] });
      qc.invalidateQueries({ queryKey: ["media", brandId] });
      qc.invalidateQueries({ queryKey: ["media-collections", brandId] });
    },
  });
}

export function useUpdateMediaAsset(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, body }: { assetId: string; body: Partial<MediaAsset> }) =>
      api.patch<{ data: MediaAsset }>(`/api/brands/${brandId}/media/${assetId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media-infinite", brandId] });
      qc.invalidateQueries({ queryKey: ["media", brandId] });
    },
  });
}

export function useDeleteMediaAsset(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => api.delete<void>(`/api/brands/${brandId}/media/${assetId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media-infinite", brandId] });
      qc.invalidateQueries({ queryKey: ["media", brandId] });
      qc.invalidateQueries({ queryKey: ["media-collections", brandId] });
    },
  });
}

export function useRenderMediaReferences(brandId: string) {
  return useMutation({
    mutationFn: (body: { text: string; context: "html" | "plain" }) =>
      api.post<{ data: { resolved: string; refs: Array<{ asset_id: string; url: string | null }> } }>(
        `/api/brands/${brandId}/render`,
        body,
      ),
  });
}

export function useShopifyIntegrations(brandId: string | undefined) {
  return useQuery({
    queryKey: ["shopify-integrations", brandId],
    queryFn: () => api.get<{ data: ShopifyIntegration[] }>(`/api/brands/${brandId}/integrations/shopify`),
    enabled: !!brandId,
  });
}

export function useEstimateShopifyImport(brandId: string) {
  return useMutation({
    mutationFn: (body: { connection_id: string }) =>
      api.post<ShopifyImportEstimateResponse>(`/api/brands/${brandId}/integrations/shopify/import`, body),
  });
}

export function useConfirmShopifyImport(brandId: string) {
  return useMutation({
    mutationFn: (body: { job_preview_id: string; confirmed_cost_cents: number }) =>
      api.post<{ data: CrawlJob }>(`/api/brands/${brandId}/integrations/shopify/import/confirm`, body),
  });
}

export function useShopifyImportJobs(brandId: string | undefined) {
  return useQuery({
    queryKey: ["shopify-import-jobs", brandId],
    queryFn: () => api.get<{ data: CrawlJob[] }>(`/api/brands/${brandId}/integrations/shopify/import`),
    enabled: !!brandId,
  });
}

export function useShopifyImportJob(brandId: string | undefined, jobId: string | undefined) {
  return useQuery({
    queryKey: ["shopify-import-job", brandId, jobId],
    queryFn: () => api.get<{ data: CrawlJob }>(`/api/brands/${brandId}/integrations/shopify/import/${jobId}`),
    enabled: !!brandId && !!jobId,
    refetchInterval: (query) => {
      return getShopifyImportJobRefetchInterval(
        query.state.data?.data,
        typeof document !== "undefined" ? document.visibilityState : "visible",
        Date.now(),
      );
    },
  });
}

export function getShopifyImportJobRefetchInterval(
  job: CrawlJob | undefined,
  visibilityState: DocumentVisibilityState | "visible" | "hidden",
  nowMs: number,
): number | false {
  if (!job || (job.status !== "queued" && job.status !== "crawling")) return false;
  if (visibilityState === "hidden") return false;
  const lastUpdateMs = job.updated_at ? new Date(job.updated_at).getTime() : new Date(job.created_at).getTime();
  const ageMs = nowMs - lastUpdateMs;
  return ageMs <= 30_000 ? 3_000 : 10_000;
}

export function useRetryFailedShopifyImport(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      api.post<{
        data: CrawlJob;
        meta?: {
          retry_batch_count?: number;
          retry_batch_image_count?: number;
          retry_batch_product_count?: number;
          remaining_retryable_image_count?: number;
          remaining_retryable_product_count?: number;
        };
      }>(`/api/brands/${brandId}/integrations/shopify/import/${jobId}/retry-failed`),
    onSuccess: (result, jobId) => {
      qc.setQueryData(["shopify-import-job", brandId, result.data.id], { data: result.data });
      qc.invalidateQueries({ queryKey: ["shopify-import-job", brandId, jobId] });
      qc.invalidateQueries({ queryKey: ["shopify-import-jobs", brandId] });
    },
  });
}

export function useRaiseShopifyImportCeiling(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, new_ceiling_cents }: { jobId: string; new_ceiling_cents: number }) =>
      api.post<{ data: CrawlJob }>(`/api/brands/${brandId}/integrations/shopify/import/${jobId}/raise-ceiling`, { new_ceiling_cents }),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: ["shopify-import-job", brandId, variables.jobId] });
      qc.invalidateQueries({ queryKey: ["shopify-import-jobs", brandId] });
    },
  });
}
